import { Grid } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroFileDragAndDrop,
	CerebroForm,
	CerebroLabel,
	CerebroMultiDropdown,
	CerebroMultiDropdownMenuItem,
	CerebroPrimaryButton,
	CerebroText,
	CerebroYesNo,
} from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { DataRoomDocument, DataRoomFolder, DocumentType, DocumentTypeMap } from '../../shared/models';
import { FormikUtils, HtmlUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getApiResponseData } from '../../shared/utils/axios';

interface AddDocumentFormProps {
	dataRoomGuid: string | undefined;
	folder: DataRoomFolder | null | undefined;
	close: () => void;
}

export const AddDocumentForm: React.FC<AddDocumentFormProps> = ({ dataRoomGuid, folder, close }) => {
	const api = useApi();
	const client = useQueryClient();
	const snackbar = useSnackbar();
	const ref = React.useRef<HTMLDivElement>(null);

	const uploadMutation = useMutation(
		async ({ values }: AddDocumentFormMutationVariables) => {
			let filesUpdate: DataRoomDocument | null = null;

			const uploads = values.uploads;

			if (uploads && uploads.length > 0) {
				let formData = new FormData();
				formData.append('documentType', values.documentType);
				formData.append('isVisibleToAll', values.isVisibleToAll ? 'true' : 'false');
				if (values.dataRoomUsers && values.dataRoomUsers.length > 0) {
					values.dataRoomUsers.forEach(user => {
						formData.append('dataRoomUsers', user.toString());
					});
				}

				uploads.forEach(upload => {
					formData.append('uploads', upload, upload.name);
				});

				const { data, error } = await getApiResponseData<DataRoomDocument>(
					api.post(CerebroApiEndpoints.Documents.Upload(dataRoomGuid, folder?.guid), formData, {
						headers: { 'Content-Type': 'multipart/form-data' },
					})
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				filesUpdate = data;
			}

			return filesUpdate;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Documents.Root(dataRoomGuid, folder?.guid));
				snackbar.enqueueSnackbar('Data room documents were added.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer ref={ref} open={folder != null} onClose={() => close()}>
			<Formik
				initialValues={{
					documentType: 'Financial_Statement' as DocumentType,
					uploads: [] as File[],
					isVisibleToAll: true,
					dataRoomUsers: [] as number[],
				}}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(uploadMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values, setFieldValue }) => {
					return (
						<CerebroForm
							flatIndicator
							encType="multipart/form-data"
							onSubmit={handleSubmit}
							sx={{ marginTop: 3, paddingRight: 3 }}
							scrollbarOffset={HtmlUtils.CalculateScrollbarOffset(ref.current)}
						>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={8}>
									<CerebroLabel required id="documentType">
										Type
									</CerebroLabel>
									<Field
										id="documentType"
										as={CerebroDropdown}
										name="documentType"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('documentType', errors, touched)}
									>
										{Object.entries(DocumentTypeMap)
											.sort((a, b) => a[1].localeCompare(b[1]))
											.map(([value, description]) => (
												<CerebroDropdownMenuItem key={value} value={value}>
													{description}
												</CerebroDropdownMenuItem>
											))}
									</Field>
									{FormikUtils.IsInvalid('documentType', errors, touched) && (
										<CerebroText variant="error">{errors.documentType}</CerebroText>
									)}
								</Grid>

								<Grid item xs={11}>
									<CerebroLabel required id="uploads">
										Attachments
									</CerebroLabel>
									<Field
										as={CerebroFileDragAndDrop}
										name="uploads"
										onlyPdf={false}
										multiple={true}
										sx={{ marginY: 3, width: '100%' }}
									/>
									{FormikUtils.IsInvalid('uploads', errors, touched) && (
										<CerebroText variant="error">{errors.uploads}</CerebroText>
									)}
								</Grid>

								<Grid item xs={8}>
									<CerebroLabel id="isVisibleToAll">All Access</CerebroLabel>
									<Field
										id="isVisibleToAll"
										as={CerebroYesNo}
										name="isVisibleToAll"
										disabled={isSubmitting}
									/>
								</Grid>

								{!values.isVisibleToAll && (
									<>
										<Grid item xs={8}>
											<CerebroLabel required id="dataRoomUsers">
												Data Room Users
											</CerebroLabel>
											<Field
												id="dataRoomUsers"
												as={CerebroMultiDropdown}
												name="dataRoomUsers"
												disabled={isSubmitting}
												error={FormikUtils.IsInvalid('dataRoomUsers', errors, touched)}
											>
												{folder?.activeDataRoomUsers.map(user => (
													<CerebroMultiDropdownMenuItem key={user.guid} value={user.id}>
														{user.fullName} ({user.bankName})
													</CerebroMultiDropdownMenuItem>
												))}
											</Field>
											{FormikUtils.IsInvalid('dataRoomUsers', errors, touched) && (
												<CerebroText variant="error">{errors.dataRoomUsers}</CerebroText>
											)}
										</Grid>
									</>
								)}

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type AddDocumentFormMutationVariables = FormikUtils.MutateOnSubmitVariables<AddDocumentFormValues>;
interface AddDocumentFormValues {
	documentType: DocumentType;
	uploads: File[];
	isVisibleToAll: boolean;
	dataRoomUsers: number[];
}
type AddDocumentFormValueErrors = FormikErrors<AddDocumentFormValues>;

function validateForm(values: AddDocumentFormValues) {
	const errors: AddDocumentFormValueErrors = {};

	if (!values.uploads || values.uploads.length == 0) {
		errors.uploads = 'Must select at least one supported file.';
	}

	if (!values.isVisibleToAll) {
		if (!values.dataRoomUsers || values.dataRoomUsers.length == 0) {
			errors.dataRoomUsers = 'Must select at least one data room user.';
		}
	}

	return errors;
}
